import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdEdit, MdDelete, MdAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface SubIndustry {
    name: string;
    sub?: string[];
}

interface Industry {
    _id: string;
    name: string;
    description?: string;
    forWho?: string;
    pricing: string[];
    fields: string[];
    mandatoryFields: string[];
    steps?: number;
    subIndustries: SubIndustry[];
    createdAt: string;
    updatedAt: string;
}

export default function BackOfficeIndustries() {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchIndustries();
    }, []);

    const fetchIndustries = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/industry`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIndustries(response.data);
        } catch (error) {
            console.error('Error fetching industries:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${CONFIG.apiUrl}/industry/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIndustries(industries.filter(ind => ind._id !== id));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting industry:', error);
            alert('Failed to delete industry');
        }
    };

    const filteredIndustries = industries.filter((industry) => {
        const matchesSearch =
            industry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            industry.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchesSearch;
    });

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Industries Management
                    </div>
                    <button
                        onClick={() => navigate('/back-office/industries/create')}
                        className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
                    >
                        <MdAdd className="text-lg" />
                        Add Industry
                    </button>
                </div>

                <div className="mb-5">
                    <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MdSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search industries..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Name
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Sub-Industries
                                    </th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Fields
                                    </th>
                                    <th className="pb-3 text-right text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredIndustries.map((industry) => (
                                    <tr key={industry._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                                            {industry.name}
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {industry.subIndustries.length} sub-industries
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {industry.fields.length + industry.mandatoryFields.length} fields
                                        </td>
                                        <td className="py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => navigate(`/back-office/industries/edit/${industry._id}`)}
                                                    className="rounded-lg bg-blue-500 p-2 text-white hover:bg-blue-600"
                                                    title="Edit"
                                                >
                                                    <MdEdit />
                                                </button>
                                                {deleteConfirm === industry._id ? (
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => handleDelete(industry._id)}
                                                            className="rounded bg-red-500 px-3 py-1 text-xs text-white hover:bg-red-600"
                                                        >
                                                            Confirm
                                                        </button>
                                                        <button
                                                            onClick={() => setDeleteConfirm(null)}
                                                            className="rounded bg-gray-500 px-3 py-1 text-xs text-white hover:bg-gray-600"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setDeleteConfirm(industry._id)}
                                                        className="rounded-lg bg-red-500 p-2 text-white hover:bg-red-600"
                                                        title="Delete"
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredIndustries.length === 0 && (
                            <div className="py-10 text-center text-gray-500">
                                No industries found
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
